import { DOCUMENT } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    LOCALE_ID,
    Signal,
    WritableSignal,
    afterNextRender,
    computed,
    inject,
    signal,
    viewChild,
    viewChildren,
} from '@angular/core';
import { RouterLink } from '@angular/router';

import { BlockDirective, EStorageTypes, ElemDirective, ModDirective, StorageService, WINDOW } from '@rt-tools/core';
import { RtButtonDirective } from '@rt-tools/ui-kit-v2';
import { GeometryEngineService } from '@stair-craft/calc/util';
import { SiteHeaderStateService } from '@stair-craft/common/components';
import { IStairConfig, buildInitialConfig } from '@stair-craft/common/util';

import { IHeroCard, heroCards } from './home-hero.cards';
import {
    EHeroHint,
    EHeroVariant,
    HERO_HEADER_EDGE_PX,
    HERO_HINT_FADE,
    HERO_MEDIA_BASE,
    HERO_STORAGE_KEY,
    HERO_VARIANTS,
    HERO_VARIANT_ATTR,
    IHeroPart,
    heroEase,
    heroFadeWindow,
    heroHint,
    heroHoldStalled,
    heroIntroShown,
    heroLoadedProgressMax,
    heroPosterUrl,
    heroProgress,
    heroReadyPrefix,
    heroScrollCap,
    heroVideoSize,
    isHeroVariant,
    pickHeroVariant,
} from './home-hero.logic';
import { HeroPlayer, IHeroPlayerSource, heroFrameLoop, heroPartsDownloaded, heroPrimeOnGestures } from './home-hero.player';

const BEM_BLOCK: string = 'sc-home-hero';
const CARD_ON: string = `${BEM_BLOCK}__card--on`;
/** A phone or a tablet: smaller video parts */
const PHONE_QUERY: string = '(max-width: 900px), (pointer: coarse)';
const REDUCED_MOTION_QUERY: string = '(prefers-reduced-motion: reduce)';
/** The download starts on `load` or on the first of these from the person, whichever comes first */
const START_EVENTS: readonly string[] = ['pointerdown', 'touchstart', 'wheel', 'keydown'];
/** A key pressed on these does its own job (a button press, a link, typing) and is never held */
const KEY_OWNERS: string = 'a, button, input, textarea, select, summary, [contenteditable], [role], [tabindex]';
/** Keys that scroll the page down or up; held back while the first part is not ready */
const DOWN_KEYS: ReadonlySet<string> = new Set<string>(['ArrowDown', 'PageDown', 'End', ' ', 'Spacebar']);
const UP_KEYS: ReadonlySet<string> = new Set<string>(['ArrowUp', 'PageUp', 'Home']);
/** How often the stall of the download is checked, milliseconds */
const STALL_CHECK_MS: number = 1000;
/** How far the intro rises while it fades, and how far a card sinks while hidden, pixels */
const INTRO_SHIFT_PX: number = 24;
const CARD_SHIFT_PX: number = 18;
/** A random 32-bit integer divided by this lands in 0..1 */
const RANDOM_RANGE: number = 2 ** 32;

interface IHeroPoster {
    readonly variant: EHeroVariant;
    readonly large: string;
    readonly small: string;
}

/**
 * The home page hero: a video scrubbed by scroll, one of four variants. The stage sticks to the
 * window, the video goes frame by frame with the scroll, the cards stand on the progress scale. The
 * title, the lede and the buttons come from the server; the poster of the variant picked in the page
 * head is the largest paint. The video starts downloading on `load` or on the first action of the
 * person. Until its first part is ready the page does not scroll and the hint shows the percent;
 * after that the scroll stops at the end of what is downloaded. A failed or stalled download lifts
 * both holds for good
 */
@Component({
    selector: 'sc-home-hero',
    templateUrl: './home-hero.component.html',
    styleUrl: './home-hero.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        // angular
        RouterLink,

        // rt-tools
        BlockDirective,
        ElemDirective,
        ModDirective,
        RtButtonDirective,
    ],
    host: {
        class: BEM_BLOCK,
    },
})
export class HomeHeroComponent {
    readonly #window: Window & typeof globalThis = inject(WINDOW) as Window & typeof globalThis;
    readonly #document: Document = inject(DOCUMENT);
    readonly #destroyRef: DestroyRef = inject(DestroyRef);
    readonly #host: ElementRef<HTMLElement> = inject<ElementRef<HTMLElement>>(ElementRef);
    readonly #storage: StorageService = inject(StorageService);
    readonly #headerState: SiteHeaderStateService = inject(SiteHeaderStateService);
    readonly #engine: GeometryEngineService = inject(GeometryEngineService);
    readonly #locale: string = inject(LOCALE_ID);

    #player: HeroPlayer | null = null;
    /** Eased progress the scene is drawn at */
    #progress: number = 0;
    /** The page does not scroll: the first part is not ready yet */
    #locked: boolean = false;
    /** Both holds are lifted for good: everything is downloaded, the download failed or stalled */
    #free: boolean = false;
    /** When the last byte arrived, for the stall check */
    #lastBytesAt: number = 0;
    /** Lifts the wheel, touch, key and scroll holds; `null` — none were set */
    #holds: AbortController | null = null;

    protected readonly intro: Signal<ElementRef<HTMLElement>> = viewChild.required<ElementRef<HTMLElement>>('intro');
    protected readonly shadeIntro: Signal<ElementRef<HTMLElement>> = viewChild.required<ElementRef<HTMLElement>>('shadeIntro');
    protected readonly railFill: Signal<ElementRef<HTMLElement>> = viewChild.required<ElementRef<HTMLElement>>('railFill');
    protected readonly hintBox: Signal<ElementRef<HTMLElement>> = viewChild.required<ElementRef<HTMLElement>>('hintBox');
    protected readonly videos: Signal<readonly ElementRef<HTMLVideoElement>[]> = viewChildren<ElementRef<HTMLVideoElement>>('video');
    protected readonly cardNodes: Signal<readonly ElementRef<HTMLElement>[]> = viewChildren<ElementRef<HTMLElement>>('card');

    protected readonly Hint: typeof EHeroHint = EHeroHint;
    /** All four posters are in the markup; the styles show the one the page head picked, the rest are never fetched */
    protected readonly posters: readonly IHeroPoster[] = Object.values(EHeroVariant).map((variant: EHeroVariant): IHeroPoster => ({
        variant,
        large: heroPosterUrl(variant, 1920),
        small: heroPosterUrl(variant, 1280),
    }));
    /** The picked variant; unknown on the server, so the video and the cards are drawn in the browser only */
    protected readonly variant: WritableSignal<EHeroVariant | null> = signal<EHeroVariant | null>(null);
    protected readonly parts: Signal<readonly IHeroPart[]> = computed((): readonly IHeroPart[] => {
        const variant: EHeroVariant | null = this.variant();
        return variant ? HERO_VARIANTS[variant].parts : [];
    });
    protected readonly cards: Signal<readonly IHeroCard[]> = computed((): readonly IHeroCard[] => {
        const variant: EHeroVariant | null = this.variant();
        if (!variant) {
            return [];
        }
        const config: IStairConfig = buildInitialConfig(variant === EHeroVariant.A || variant === EHeroVariant.B ? 'l-shape' : 'spiral');
        return heroCards(variant, { config, result: this.#engine.calculate(config) }, this.#locale);
    });
    /** The video part on screen; `-1` — none yet, the poster stays */
    protected readonly shownPart: WritableSignal<number> = signal<number>(-1);
    protected readonly hint: WritableSignal<EHeroHint> = signal<EHeroHint>(EHeroHint.Scroll);
    protected readonly percent: WritableSignal<number> = signal<number>(0);

    constructor() {
        // The hero is the page top from the server render on: the header lies over it, transparent
        this.#headerState.overlay.set(true);
        this.#headerState.overVideo.set(true);
        this.#destroyRef.onDestroy((): void => {
            this.#headerState.overlay.set(false);
            this.#headerState.overVideo.set(false);
        });
        afterNextRender((): void => {
            this.#mount();
        });
    }

    #mount(): void {
        const win: Window & typeof globalThis = this.#window;
        const abort: AbortController = new win.AbortController();
        const variant: EHeroVariant = this.#pickVariant();
        const phone: boolean = win.matchMedia(PHONE_QUERY).matches;
        const reduced: boolean = win.matchMedia(REDUCED_MOTION_QUERY).matches;
        const source: IHeroPlayerSource = { base: HERO_MEDIA_BASE, size: heroVideoSize(phone, win.innerWidth, win.devicePixelRatio) };
        this.variant.set(variant);

        // A hash that names no variant leads to a block below the hero, and a video already downloaded
        // during this visit plays at once: no hold, and the router keeps the place it restored
        const toBlock: boolean = !!win.location.hash && !isHeroVariant(win.location.hash.slice(1).toLowerCase());
        if (toBlock || heroPartsDownloaded(source, HERO_VARIANTS[variant].parts)) {
            this.#free = true;
        } else {
            this.#lock();
            this.#listenHolds(abort.signal);
        }

        // The videos are drawn after the variant is known; until then a start has nothing to fill
        const startAbort: AbortController = new win.AbortController();
        abort.signal.addEventListener('abort', (): void => {
            startAbort.abort();
        });
        // `load` can come before the videos are drawn for the variant: the start is tried again on the
        // next frame until they are there, or the page holds its lock with nothing downloading
        const start: () => void = (): void => {
            if (this.#start(variant, source)) {
                startAbort.abort();
                return;
            }
            if (!startAbort.signal.aborted) {
                win.requestAnimationFrame(start);
            }
        };
        for (const type of START_EVENTS) {
            win.addEventListener(type, start, { passive: true, signal: startAbort.signal });
        }
        if (this.#document.readyState === 'complete') {
            start();
        } else {
            win.addEventListener('load', start, { once: true, signal: startAbort.signal });
        }
        heroPrimeOnGestures(win, (): HeroPlayer | null => this.#player, abort.signal);
        const stallId: number = win.setInterval((): void => {
            if (this.#player && !this.#free && heroHoldStalled(win.performance.now(), this.#lastBytesAt, this.#player.openWaitSince())) {
                this.#release();
            }
        }, STALL_CHECK_MS);

        // Frames are drawn only while the hero is on screen; below the fold the header stands solid
        const stopLoop: () => void = heroFrameLoop(
            win,
            this.#host.nativeElement,
            (seconds: number): void => {
                this.#draw(seconds, reduced);
            },
            (): void => {
                this.#headerState.overVideo.set(false);
            }
        );

        this.#destroyRef.onDestroy((): void => {
            abort.abort();
            stopLoop();
            win.clearInterval(stallId);
            this.#player?.stop();
            this.#unlock();
        });
    }

    /**
     * Wheel, touch and keys are held while locked, and past the downloaded end while not free. The
     * listeners live only until the release: after it the page scrolls with no handler of ours
     */
    #listenHolds(pageLeave: AbortSignal): void {
        const win: Window & typeof globalThis = this.#window;
        const holds: AbortController = new win.AbortController();
        pageLeave.addEventListener('abort', (): void => {
            holds.abort();
        });
        this.#holds = holds;
        const passive: AddEventListenerOptions = { passive: true, signal: holds.signal };
        const active: AddEventListenerOptions = { passive: false, signal: holds.signal };
        let touchY: number = 0;
        win.addEventListener(
            'touchstart',
            (event: TouchEvent): void => {
                touchY = event.touches[0].clientY;
            },
            passive
        );
        win.addEventListener(
            'touchmove',
            (event: TouchEvent): void => {
                if (this.#held(event.touches[0].clientY < touchY)) {
                    event.preventDefault();
                }
            },
            active
        );
        win.addEventListener(
            'wheel',
            (event: WheelEvent): void => {
                if (this.#held(event.deltaY > 0)) {
                    event.preventDefault();
                }
            },
            active
        );
        win.addEventListener(
            'keydown',
            (event: KeyboardEvent): void => {
                this.#guardKey(event);
            },
            active
        );
        // The scrollbar and a jump by anchor are not held by the events above: the position is pulled back
        win.addEventListener(
            'scroll',
            (): void => {
                const cap: number = this.#scrollCap();
                if (win.scrollY > cap + 1) {
                    win.scrollTo(0, cap);
                }
            },
            passive
        );
        // Focus moved by Tab to a block below the hero: the person goes past the video, the holds are lifted
        win.addEventListener(
            'focusin',
            (event: FocusEvent): void => {
                if (this.#isBelowHero(event.target)) {
                    this.#release();
                }
            },
            passive
        );
    }

    #isBelowHero(target: EventTarget | null): boolean {
        const host: HTMLElement = this.#host.nativeElement;
        return (
            target instanceof this.#window.Element &&
            !host.contains(target) &&
            target.getBoundingClientRect().top >= host.getBoundingClientRect().bottom
        );
    }

    /** A move is held while locked, and a move down at the downloaded end while not free */
    #held(down: boolean): boolean {
        return !this.#free && (this.#locked || (down && this.#atWall()));
    }

    #guardKey(event: KeyboardEvent): void {
        const target: EventTarget | null = event.target;
        // Space on a button presses it, an arrow in a field moves the caret: those keys are not held
        const owned: boolean = target instanceof this.#window.Element && target.closest(KEY_OWNERS) !== null;
        if (this.#free || owned) {
            return;
        }
        const down: boolean = DOWN_KEYS.has(event.key);
        if ((down || UP_KEYS.has(event.key)) && this.#held(down)) {
            event.preventDefault();
        }
    }

    /** The page scroll the downloaded parts allow; no limit once free */
    #scrollCap(): number {
        if (this.#free) {
            return Number.POSITIVE_INFINITY;
        }
        const progressMax: number = heroLoadedProgressMax(this.parts(), this.#player?.ready() ?? []);
        if (progressMax === 0) {
            return 0;
        }
        const host: HTMLElement = this.#host.nativeElement;
        const top: number = host.getBoundingClientRect().top + this.#window.scrollY;
        return heroScrollCap(top, host.offsetHeight, this.#window.innerHeight, progressMax);
    }

    #atWall(): boolean {
        return this.#window.scrollY >= this.#scrollCap() - 2;
    }

    /** The page stands at the top and does not scroll; the hint shows the download percent */
    #lock(): void {
        const root: HTMLElement = this.#document.documentElement;
        this.#locked = true;
        this.#window.history.scrollRestoration = 'manual';
        this.#window.scrollTo(0, 0);
        // The scrollbar keeps its room, so the page does not shift sideways when it comes back
        root.style.scrollbarGutter = this.#window.innerWidth > root.clientWidth ? 'stable' : '';
        root.style.overflow = 'hidden';
        root.style.overscrollBehavior = 'none';
        this.hint.set(EHeroHint.Loading);
    }

    #unlock(): void {
        const root: HTMLElement = this.#document.documentElement;
        this.#locked = false;
        root.style.removeProperty('overflow');
        root.style.removeProperty('scrollbar-gutter');
        root.style.removeProperty('overscroll-behavior');
    }

    /** Both holds are lifted for good */
    #release(): void {
        this.#free = true;
        this.#holds?.abort();
        this.#unlock();
        this.hint.set(EHeroHint.Scroll);
    }

    /**
     * The same rule as the page head, on every mount: the address, then the variant kept for this tab,
     * then the one the head marked on `<html>` (storage closed in a private window), then a random one
     * that is stored. A move inside the app to `/#c` thus shows C, and the way back shows the kept one
     */
    #pickVariant(): EHeroVariant {
        const root: HTMLElement = this.#document.documentElement;
        const marked: string | null = root.getAttribute(HERO_VARIANT_ATTR);
        const pick: { variant: EHeroVariant; store: boolean } = pickHeroVariant(
            this.#window.location.hash,
            this.#readStored() ?? marked,
            this.#window.crypto.getRandomValues(new Uint32Array(1))[0] / RANDOM_RANGE
        );
        if (pick.store) {
            try {
                // Per tab: a reload keeps the variant, a new tab picks again
                this.#storage.setItem(HERO_STORAGE_KEY, pick.variant, { ctx: EStorageTypes.SESSION });
            } catch {
                // Storage is closed in a private window: the variant lives until the reload
            }
        }
        root.setAttribute(HERO_VARIANT_ATTR, pick.variant);
        return pick.variant;
    }

    #readStored(): string | null {
        try {
            return this.#storage.getItem<string>(HERO_STORAGE_KEY, { ctx: EStorageTypes.SESSION }) ?? null;
        } catch {
            return null;
        }
    }

    /** Starts the download once the videos are in the markup; `true` — started */
    #start(variant: EHeroVariant, source: IHeroPlayerSource): boolean {
        if (this.#player) {
            return true;
        }
        const videos: HTMLVideoElement[] = this.videos().map(
            (video: ElementRef<HTMLVideoElement>): HTMLVideoElement => video.nativeElement
        );
        if (videos.length === 0) {
            return false;
        }
        const win: Window & typeof globalThis = this.#window;
        const player: HeroPlayer = new HeroPlayer(win, videos, HERO_VARIANTS[variant].parts, source, {
            loading: (): void => {
                this.#lastBytesAt = win.performance.now();
                this.percent.set(player.percent());
                const ready: readonly boolean[] = player.ready();
                if (ready[0] && this.#locked) {
                    this.#unlock();
                }
                if (heroReadyPrefix(ready) === ready.length) {
                    this.#release();
                }
                if (!this.#free) {
                    this.hint.set(heroHint(true, ready));
                }
            },
            shown: (part: number): void => {
                this.shownPart.set(part);
            },
            failed: (): void => {
                this.#release();
            },
        });
        this.#player = player;
        this.#lastBytesAt = win.performance.now();
        player.start();
        if (!this.#free) {
            this.hint.set(heroHint(true, player.ready()));
        }
        return true;
    }

    /** One animation frame: progress, video frame, header, intro, cards, rail and hint */
    #draw(seconds: number, reduced: boolean): void {
        const win: Window & typeof globalThis = this.#window;
        const rect: DOMRect = this.#host.nativeElement.getBoundingClientRect();
        const target: number = heroProgress(rect.top, rect.height, win.innerHeight);
        this.#progress = heroEase(this.#progress, target, seconds, reduced);
        this.#headerState.overVideo.set(rect.bottom > HERO_HEADER_EDGE_PX);
        if (rect.bottom <= 0 || rect.top >= win.innerHeight) {
            return;
        }
        const progress: number = this.#progress;
        this.#player?.scrub(progress);

        const introShown: number = heroIntroShown(progress);
        const intro: CSSStyleDeclaration = this.intro().nativeElement.style;
        intro.opacity = introShown.toFixed(3);
        intro.transform = `translateY(${(-INTRO_SHIFT_PX * (1 - introShown)).toFixed(1)}px)`;
        intro.visibility = introShown < 0.01 ? 'hidden' : 'visible';
        this.shadeIntro().nativeElement.style.opacity = introShown.toFixed(3);

        const cards: readonly IHeroCard[] = this.cards();
        this.cardNodes().forEach((node: ElementRef<HTMLElement>, index: number): void => {
            // The card nodes are drawn from the same list, so the index always has its card
            const card: IHeroCard = cards[index];
            const shown: number = heroFadeWindow(progress, card.from, card.to);
            const style: CSSStyleDeclaration = node.nativeElement.style;
            style.opacity = shown.toFixed(3);
            style.transform = `translateY(${((1 - shown) * CARD_SHIFT_PX).toFixed(1)}px)`;
            style.visibility = shown < 0.01 ? 'hidden' : 'visible';
            node.nativeElement.classList.toggle(CARD_ON, shown > 0.5);
        });

        this.railFill().nativeElement.style.transform = `scaleY(${progress.toFixed(4)})`;
        this.hintBox().nativeElement.style.opacity = target > HERO_HINT_FADE ? '0' : '1';
    }
}
